import { useMemo } from "react";

import { Badge, BadgeColor, TableRow } from "@prequel/shared-components";

import { ApiRoutes } from "@/api/client";
import { useAllProducts } from "@/api/queries/products";
import { useAllRecipients, useRecipients } from "@/api/queries/recipients";
import ResourceTable, { type FilterField } from "@/components/ResourceTable";
import { ExistingRecipient } from "@/types";

const RECIPIENT_TABLE_COLUMNS = ["Recipient Name", "ID in Provider System", "Products"];

const renderProductBadges = (products: string[]) => (
  <>
    {products.map((p) => (
      <Badge key={p} className="mr-1" text={p} color={BadgeColor.GRAY} />
    ))}
  </>
);

const rowMapper = (recipient: ExistingRecipient): TableRow => ({
  id: recipient.id,
  to: `${ApiRoutes.RECIPIENTS}/${recipient.id}`,
  cells: [
    { value: recipient.name },
    { value: recipient.id_in_provider_system },
    { value: renderProductBadges(recipient.products) },
  ],
});

const RecipientsTable = () => {
  const rowMapperMemo = useMemo(() => rowMapper, []);
  const { data: products = [], isLoading: productsLoading } = useAllProducts();
  const { data: allRecipients, isLoading: recipientsLoading } = useAllRecipients();

  const filterFields: FilterField[] = useMemo(
    () => [
      {
        key: "id_in_provider_system",
        label: "ID in Provider System",
        queryParam: "id_in_provider_system",
        loading: recipientsLoading,
        options: (allRecipients ?? []).map((r) => ({ value: r.id_in_provider_system, label: r.id_in_provider_system })),
      },
      {
        key: "products",
        label: "Product",
        queryParam: "product_name",
        loading: productsLoading,
        options: products.map((p) => ({ value: p.product_name, label: p.product_name })),
      },
    ],
    [products, productsLoading, allRecipients, recipientsLoading],
  );

  return (
    <ResourceTable<ExistingRecipient>
      columns={RECIPIENT_TABLE_COLUMNS}
      useQuery={useRecipients}
      rowMapper={rowMapperMemo}
      emptyMessage="No recipients found. Recipients will show up here."
      filterFields={filterFields}
    />
  );
};

export default RecipientsTable;
